import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  localizationService,
  type LocalePreference,
  type RegionConfig,
  type TranslationParams,
} from '../services/localizationService';

export interface LocaleContextValue {
  /** Current UI language code, e.g. `en`, `am`. */
  language: string;
  /** Current region code, e.g. `US`, `ET`. */
  region: string;
  /** Formatting rules for the current region. */
  regionConfig: RegionConfig;
  /** Translate a dot-path key with optional `{param}` interpolation. */
  t: (key: string, params?: TranslationParams) => string;
  setLanguage: (language: string) => void;
  setRegion: (region: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- context object, mirrors AuthContext
export const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Provides the current language/region and a `t()` function to the whole app.
 * State lives in `localizationService` (persisted to localStorage), so changes
 * made outside React — e.g. the settings store syncing a saved preference —
 * propagate here automatically.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [pref, setPref] = useState<LocalePreference>(() => localizationService.getLocale());

  useEffect(() => localizationService.subscribe(setPref), []);

  // Reflect the language on <html>. Amharic uses the Ethiopic script but is
  // written left-to-right, so dir stays "ltr" for every supported language.
  useEffect(() => {
    document.documentElement.lang = pref.language;
    document.documentElement.dir = 'ltr';
  }, [pref.language]);

  const t = useCallback(
    (key: string, params?: TranslationParams) =>
      localizationService.translate(pref.language, key, params),
    [pref.language],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({
      language: pref.language,
      region: pref.region,
      regionConfig: localizationService.regionConfig(pref.region),
      t,
      setLanguage: (language) => localizationService.setLocale({ language }),
      setRegion: (region) => localizationService.setLocale({ region }),
    }),
    [pref.language, pref.region, t],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
